import React, { useState } from "react";
import Reveal from "./Reveal";
import { BedDouble, Bath, Home, Umbrella, Sprout, Car, Check, MapPin, MessageCircle, FileImage } from "lucide-react";
import { BELAIR, CONTACT } from "../mock";

const ICONS = { BedDouble, Bath, Home, Umbrella, Sprout, Car };

const LangToggle = ({ lang, setLang }) => (
  <div className="self-start lg:self-auto inline-flex border border-[#18324F]/20 rounded-full p-1" data-testid="belair-lang-toggle">
    {["en", "es"].map((l) => (
      <button
        key={l}
        data-testid={`belair-lang-${l}`}
        onClick={() => setLang(l)}
        className={`px-4 py-1.5 rounded-full text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
          lang === l ? "bg-[#18324F] text-[#FAF8F3]" : "text-[#18324F]/70 hover:text-[#B88A3B]"
        }`}
      >
        {l}
      </button>
    ))}
  </div>
);

const Specs = ({ lang }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-y border-[#18324F]/10 mt-14" data-testid="belair-specs">
    {BELAIR.specs.map((s, i) => {
      const Icon = ICONS[s.icon];
      return (
        <div key={s.en} className={`py-8 px-4 text-center ${i ? "lg:border-l" : ""} border-[#18324F]/10`}>
          <Icon className="mx-auto text-[#B88A3B]" size={28} strokeWidth={1.3} />
          <p className="font-serif-lux text-3xl mt-4">{s.value}</p>
          <p className="uppercase tracking-[0.18em] text-[11px] text-[#18324F]/60 mt-2">{s[lang]}</p>
        </div>
      );
    })}
  </div>
);

const Gallery = ({ t }) => (
  <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mt-14" data-testid="belair-gallery">
    {BELAIR.gallery.map((g, i) => (
      <figure
        key={g.key}
        className={`relative overflow-hidden group ${i === 0 ? "col-span-2 lg:col-span-4 aspect-[16/9]" : i === 1 ? "col-span-2 lg:col-span-2 aspect-[4/3] lg:aspect-auto" : "lg:col-span-2 aspect-[4/3]"}`}
      >
        <img src={g.src} alt={t.galleryLabels[g.key]} data-testid={`belair-gallery-${g.key}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <figcaption className="absolute left-3 bottom-3 bg-[#FAF8F3]/90 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-[0.2em]">
          {t.galleryLabels[g.key]}
        </figcaption>
      </figure>
    ))}
  </div>
);

const Details = ({ t }) => (
  <div className="grid lg:grid-cols-3 gap-12 mt-20">
    <div>
      <MapPin className="text-[#B88A3B]" size={26} strokeWidth={1.3} />
      <p className="uppercase tracking-[0.3em] text-sm mt-4">Bel Air · Marbella</p>
      <p className="font-serif-lux italic text-3xl text-[#B88A3B] mt-6 leading-snug">{t.quote}</p>
    </div>
    <div className="lg:border-l lg:pl-12 border-[#18324F]/10">
      <h3 className="font-serif-lux text-2xl font-medium">{t.descTitle}</h3>
      <p className="text-[#18324F]/70 mt-5 leading-relaxed" data-testid="belair-description">{t.desc}</p>
    </div>
    <div className="lg:border-l lg:pl-12 border-[#18324F]/10">
      <p className="uppercase tracking-[0.3em] text-xs text-[#B88A3B]">{t.featuresTitle}</p>
      <ul className="mt-5 space-y-3" data-testid="belair-features">
        {t.features.map((f) => (
          <li key={f} className="flex items-center gap-3 text-[#18324F]/80">
            <Check size={16} className="text-[#B88A3B]" /> {f}
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Hero = ({ t }) => (
  <div className="relative mt-14 overflow-hidden">
    <img src={BELAIR.hero} alt="Bel Air terrace" data-testid="belair-hero-image" className="w-full aspect-[4/3] lg:aspect-[21/9] object-cover" />
    <div className="absolute top-4 right-4 lg:top-8 lg:right-8 bg-[#18324F] text-[#FAF8F3] px-6 py-4 lg:px-10 lg:py-6 text-center" data-testid="belair-price">
      <p className="uppercase tracking-[0.3em] text-[10px] lg:text-xs">{t.priceLabel}</p>
      <p className="font-serif-lux text-3xl lg:text-5xl mt-1">{t.price}</p>
    </div>
    <p className="absolute left-4 bottom-4 lg:left-8 lg:bottom-8 bg-[#FAF8F3]/90 backdrop-blur px-4 py-2 font-serif-lux italic text-lg lg:text-2xl">
      {t.badge}
    </p>
  </div>
);

const Actions = ({ t }) => (
  <div className="flex flex-col sm:flex-row gap-4 mt-16">
    <a
      href={`${CONTACT.whatsapp}?text=${encodeURIComponent(t.waText)}`}
      target="_blank"
      rel="noreferrer"
      data-testid="belair-whatsapp-btn"
      className="inline-flex items-center justify-center gap-3 bg-[#B88A3B] text-white px-8 py-4 text-sm uppercase tracking-[0.2em] hover:bg-[#18324F] transition-colors duration-300"
    >
      <MessageCircle size={18} /> {t.enquire}
    </a>
    <a
      href={BELAIR.flyer}
      target="_blank"
      rel="noreferrer"
      data-testid="belair-flyer-btn"
      className="inline-flex items-center justify-center gap-3 border border-[#18324F]/30 px-8 py-4 text-sm uppercase tracking-[0.2em] hover:border-[#B88A3B] hover:text-[#B88A3B] transition-colors duration-300"
    >
      <FileImage size={18} /> {t.viewFlyer}
    </a>
  </div>
);

const BelAir = () => {
  const [lang, setLang] = useState("en");
  const t = BELAIR[lang];
  return (
    <section id="bel-air" data-testid="belair-section" className="bg-[#F3EEE5] text-[#18324F] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <Reveal>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div>
              <p className="uppercase tracking-[0.3em] text-xs text-[#B88A3B]">{t.eyebrow}</p>
              <h2 className="font-serif-lux font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-6" data-testid="belair-title">
                Bel Air <span className="italic text-[#B88A3B]">Marbella</span>
              </h2>
              <p className="uppercase tracking-[0.25em] text-sm text-[#18324F]/70 mt-4">{t.tagline}</p>
            </div>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </Reveal>
        <Reveal><Hero t={t} /></Reveal>
        <p className="text-center uppercase tracking-[0.35em] text-xs text-[#18324F]/60 mt-10">{t.pillars}</p>
        <Reveal><Specs lang={lang} /></Reveal>
        <Reveal><Gallery t={t} /></Reveal>
        <Reveal><Details t={t} /></Reveal>
        <Actions t={t} />
      </div>
    </section>
  );
};

export default BelAir;
